import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, TrendingUp, Target, Calendar, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useUser } from "@/contexts/UserContext";
import { wellnessService } from "@/services/wellnessService";

export default function WelcomeBackCard() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [checkInCount, setCheckInCount] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserProgress = async () => {
      if (!user?.id) return;

      try {
        // apiService returns data directly, not { success, data }
        const statsData = await wellnessService.getStats().catch(() => ({ totalCheckIns: 0 }));
        const count = statsData?.totalCheckIns || 0;
        setCheckInCount(count);
        
        // Show welcome message for users with 1-3 check-ins
        setShowWelcome(count >= 1 && count <= 3);
      } catch (error) {
        console.error("Error loading user progress:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProgress();
  }, [user?.id]);

  if (isLoading || !showWelcome) {
    return null;
  }

  const getWelcomeMessage = () => {
    if (checkInCount === 1) {
      return {
        title: "🎉 Great start!",
        message: "You've completed your first check-in. Your dashboard is now fully active with insights and personalized suggestions.",
        action: "Explore Features",
        actionFn: () => navigate('/patterns')
      };
    } else if (checkInCount === 2) {
      return {
        title: "🔥 Building momentum!",
        message: "Two check-ins done! You're developing a great habit. Keep going to unlock deeper insights.",
        action: "Continue Journey",
        actionFn: () => navigate('/check-in')
      };
    } else {
      return {
        title: "🚀 You're on fire!",
        message: "Three check-ins completed! Your patterns are starting to emerge. Ready to set your first goal?",
        action: "Set Goals",
        actionFn: () => navigate('/profile', { state: { activeTab: 'goals' } })
      };
    }
  };

  const welcomeData = getWelcomeMessage();

  return (
    <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-green-100 rounded-xl">
            <Sparkles className="h-6 w-6 text-green-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-green-900">{welcomeData.title}</h3>
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                {checkInCount} check-in{checkInCount > 1 ? 's' : ''} completed
              </Badge>
            </div>
            <p className="text-sm text-green-700 mb-4">
              {welcomeData.message}
            </p>
            <div className="flex items-center gap-4">
              <Button 
                onClick={welcomeData.actionFn}
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
              >
                {welcomeData.action}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <div className="flex items-center gap-3 text-xs text-green-600">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>Growing</span>
                </div>
                <div className="flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  <span>Focused</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Consistent</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}